import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
	TrendingUp,
	Clock,
	Flame,
	Search,
	X,
	SlidersHorizontal,
} from "lucide-react";
import { IdeaCard } from "./IdeaCard";
import type { Idea, User, SortOption } from "@/lib/types";
import { INDUSTRY_TAGS } from "@/lib/types";

interface IdeaFeedProps {
	ideas: Idea[];
	users: User[];
	currentUserId: string;
	onVote: (ideaId: string) => void;
	onSelectIdea: (ideaId: string) => void;
}

export function IdeaFeed({
	ideas,
	users,
	currentUserId,
	onVote,
	onSelectIdea,
}: IdeaFeedProps) {
	const [sort, setSort] = useState<SortOption>("trending");
	const [search, setSearch] = useState("");
	const [selectedTags, setSelectedTags] = useState<string[]>([]);
	const [showTags, setShowTags] = useState(false);

	const userMap = useMemo(() => {
		const m = new Map<string, User>();
		for (const u of users) m.set(u.id, u);
		return m;
	}, [users]);

	const filteredIdeas = useMemo(() => {
		let result = [...ideas];

		// Search
		if (search) {
			const q = search.toLowerCase();
			result = result.filter(
				(i) =>
					i.title.toLowerCase().includes(q) ||
					i.problem.toLowerCase().includes(q) ||
					i.solution.toLowerCase().includes(q) ||
					i.tags.some((t) => t.toLowerCase().includes(q)),
			);
		}

		// Tag filter
		if (selectedTags.length > 0) {
			result = result.filter((i) =>
				selectedTags.some((t) => i.tags.includes(t)),
			);
		}

		// Sort
		switch (sort) {
			case "trending":
				result.sort((a, b) => {
					const ageA =
						(Date.now() - new Date(a.createdAt).getTime()) / 3600000;
					const ageB =
						(Date.now() - new Date(b.createdAt).getTime()) / 3600000;
					const scoreA = a.votes / Math.pow(ageA + 2, 1.5);
					const scoreB = b.votes / Math.pow(ageB + 2, 1.5);
					return scoreB - scoreA;
				});
				break;
			case "newest":
				result.sort(
					(a, b) =>
						new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
				);
				break;
			case "top":
				result.sort((a, b) => b.votes - a.votes);
				break;
			case "weekly": {
				const oneWeekAgo = new Date();
				oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
				result = result.filter(
					(i) => new Date(i.createdAt) >= oneWeekAgo,
				);
				result.sort((a, b) => b.votes - a.votes);
				break;
			}
		}

		return result;
	}, [ideas, search, selectedTags, sort]);

	const toggleTag = (tag: string) => {
		setSelectedTags((prev) =>
			prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
		);
	};

	const sortOptions: { value: SortOption; label: string; icon: React.ReactNode }[] = [
		{
			value: "trending",
			label: "Trending",
			icon: <TrendingUp className="w-3.5 h-3.5" />,
		},
		{
			value: "newest",
			label: "New",
			icon: <Clock className="w-3.5 h-3.5" />,
		},
		{
			value: "top",
			label: "Top",
			icon: <Flame className="w-3.5 h-3.5" />,
		},
		{
			value: "weekly",
			label: "This Week",
			icon: <TrendingUp className="w-3.5 h-3.5" />,
		},
	];

	return (
		<div className="space-y-4">
			{/* Search and sort bar */}
			<div className="flex flex-col sm:flex-row gap-3">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
					<Input
						placeholder="Search ideas, problems, solutions..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-10 h-10"
					/>
					{search && (
						<button
							onClick={() => setSearch("")}
							className="absolute right-3 top-1/2 -translate-y-1/2"
						>
							<X className="w-4 h-4 text-muted-foreground" />
						</button>
					)}
				</div>

				<div className="flex items-center gap-1.5">
					{sortOptions.map((opt) => (
						<Button
							key={opt.value}
							variant={sort === opt.value ? "default" : "ghost"}
							size="sm"
							onClick={() => setSort(opt.value)}
							className={`gap-1.5 text-xs ${
								sort === opt.value
									? "bg-foreground text-background"
									: ""
							}`}
						>
							{opt.icon}
							{opt.label}
						</Button>
					))}

					<Separator orientation="vertical" className="h-6 mx-1" />

					<Button
						variant={showTags ? "default" : "ghost"}
						size="sm"
						onClick={() => setShowTags(!showTags)}
						className="gap-1.5 text-xs"
					>
						<SlidersHorizontal className="w-3.5 h-3.5" />
						Tags
						{selectedTags.length > 0 && (
							<Badge
								variant="secondary"
								className="ml-1 text-[10px] px-1.5 py-0 h-4"
							>
								{selectedTags.length}
							</Badge>
						)}
					</Button>
				</div>
			</div>

			{/* Tags filter */}
			{showTags && (
				<div className="flex flex-wrap gap-1.5 p-3 rounded-lg bg-muted/50 border border-border">
					{INDUSTRY_TAGS.map((tag) => (
						<Badge
							key={tag}
							variant={selectedTags.includes(tag) ? "default" : "outline"}
							className={`cursor-pointer text-xs transition-colors ${
								selectedTags.includes(tag)
									? "bg-orange-500 hover:bg-orange-600 border-orange-500 text-white"
									: "hover:border-orange-300 hover:text-orange-600"
							}`}
							onClick={() => toggleTag(tag)}
						>
							{tag}
						</Badge>
					))}
					{selectedTags.length > 0 && (
						<button
							onClick={() => setSelectedTags([])}
							className="text-xs text-muted-foreground hover:text-foreground ml-2"
						>
							Clear all
						</button>
					)}
				</div>
			)}

			{/* Results count */}
			<div className="text-sm text-muted-foreground">
				{filteredIdeas.length} idea{filteredIdeas.length !== 1 ? "s" : ""}
				{search && ` matching "${search}"`}
				{selectedTags.length > 0 &&
					` in ${selectedTags.join(", ")}`}
			</div>

			{/* Ideas list */}
			<div className="space-y-3">
				{filteredIdeas.map((idea, index) => (
					<IdeaCard
						key={idea.id}
						idea={idea}
						author={userMap.get(idea.authorId)}
						currentUserId={currentUserId}
						rank={sort === "top" || sort === "weekly" ? index + 1 : undefined}
						onVote={onVote}
						onClick={onSelectIdea}
					/>
				))}
				{filteredIdeas.length === 0 && (
					<div className="text-center py-16 text-muted-foreground">
						<p className="text-lg font-medium mb-1">No ideas found</p>
						<p className="text-sm">
							Try adjusting your search or filters
						</p>
					</div>
				)}
			</div>
		</div>
	);
}
